import React from 'react';
import { oneOf, bool, string, node, shape } from 'prop-types';
import A11yFocus from '@ids/a11yfocus';

import { getStyle as iconControlMediumStyles } from '@ids/ddms-styled/comps/iconControlMd/intuit/light';
import { getStyle as iconControlSmallStyles } from '@ids/ddms-styled/comps/iconControlSm/intuit/light';
import { getStyle as iconControlSmallStylesDark } from '@ids/ddms-styled/comps/iconControlSm/intuit/dark';
import { getStyle as iconControlLargeStyles } from '@ids/ddms-styled/comps/iconControlLg/intuit/light';
import { styleOverrideReason } from '@ids/core';
import { Label, Button } from './StyledIconControl';

// Icon Sizes.
const iconSizes = {
  small: iconControlSmallStyles('icon.height'),
  medium: iconControlMediumStyles('icon.height'),
  large: iconControlLargeStyles('background.height')
};

const IconControl = React.forwardRef((props, ref) => {
  const {
    size,
    children,
    selected,
    inverse,
    label,
    automationId,
    iconColor,
    labelColor,
    ...restProps
  } = props;

  // Calculating Width, Height & color for the Icon
  // This would be passed down to the StyledIcon and used there
  const iconSize = props.size ? iconSizes[props.size] : '';
  const margin =
    props.size === 'small' || props.size === 'medium' ? '4px' : '10px';

  const icon = {
    default: iconControlMediumStyles('icon.color'),
    inverse: iconControlSmallStylesDark('overlay.color'),
    selected: iconControlMediumStyles('icon.color.selected'),
    'inverse-and-selected': iconControlSmallStylesDark('icon.color.selected')
  };

  /**
   *  Computing the colors and passing it down to StyledIcon do not change the sequence of the below
   */
  const getColor = () => {
    if (props.inverse && props.selected) {
      return icon['inverse-and-selected'];
    }

    if (props.inverse) {
      return icon.inverse;
    }

    if (props.selected) {
      return iconColor && iconColor.selected
        ? iconColor.selected
        : icon.selected;
    }

    return iconColor && iconColor.default ? iconColor.default : icon.default;
  };

  return (
    <A11yFocus passA11yFocusProp>
      <Button
        {...restProps}
        ref={ref}
        size={size}
        label={label}
        inverse={inverse}
        selected={selected}
        aria-expanded={selected}
        data-automation-id={automationId}
        focusColor={iconColor && iconColor.focus && iconColor.focus}
        hoverColor={iconColor && iconColor.hover && iconColor.hover}
        selectedBackgroundColor={
          iconColor &&
          iconColor.selectedBackground &&
          iconColor.selectedBackground
        }
      >
        {React.cloneElement(children, {
          color: getColor(),
          width: iconSize,
          height: iconSize,
          margin
        })}
        {label && (
          <Label
            size={size}
            inverse={inverse}
            selected={selected}
            color={labelColor}
          >
            {label}
          </Label>
        )}
      </Button>
    </A11yFocus>
  );
});

IconControl.propTypes = {
  /**
   * Whether to use inverse theming, optional.
   */
  // eslint-disable-next-line react/boolean-prop-naming
  inverse: bool,
  /**
   * Specifies Button size, 'small', 'medium' or 'large'
   */
  size: oneOf(['small', 'medium', 'large']),
  /**
   * Icon to display
   */
  children: node,
  /**
   * Whether icon is selected.
   */
  // eslint-disable-next-line react/boolean-prop-naming
  selected: bool,
  /**
   * Aria label
   */
  'aria-label': string.isRequired,
  /**
   * Label for icon
   */
  label: string,
  /**
   * Position for the label
   */
  labelPosition: oneOf(['top', 'bottom', 'right', 'left']),
  /**
   * Component ID
   */
  automationId: string,
  /**
   * Configurable color of icon in various states
   */
  iconColor: shape({
    default: string,
    selected: string,
    focus: string,
    hover: string,
    selectedBackground: string
  }),
  /**
   * Configurable color of label
   */
  labelColor: shape({
    default: string,
    selected: string
  }),
  /**
   * When `style` or `className` prop is provided, provide the reason behind the override.
   */
  styleOverrideReason
};

IconControl.defaultProps = {
  automationId: 'IconControl',
  inverse: false,
  size: 'small',
  selected: null,
  children: null,
  label: '',
  labelPosition: 'right',
  iconColor: undefined,
  labelColor: undefined,
  styleOverrideReason: undefined
};

export default IconControl;
